/**
 * DriverAssignmentPage.js
 *
 * A lightweight, mobile-first page shown to users with the "driver" role
 * instead of the full SingleAssignment view.  Contains only the data, state,
 * and UI that a driver actually needs:
 *   • Customer contact & address
 *   • Deliveries assigned to this driver
 *   • Confirm-package → upload photos → end-delivery flow
 *
 * Keeping this component separate means drivers never download the admin/designer
 * bundle (notes, file management, estimation, designer assignment, etc.).
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "../../config/supabase";
import { useAuth } from "../../contexts/AuthContext";
import { shortIdToUuid } from "../../lib/common/shortId";

import {
    getSingleAssignment,
    confirmPackageReceipt,
    uploadDeliveryPhoto,
    uploadDeliveryPhotosBulk,
    deleteDeliveryPhoto,
    completeDelivery,
    subscribeToJobUpdates,
} from "../../lib/supabase/single-assignment";

import {
    sendInTransitNotificationSMS,
    sendDeliveredNotificationSMS,
} from "../../lib/supabase/sms";

import {
    FiPhone,
    FiMapPin,
    FiCalendar,
    FiUser,
    FiCheckCircle,
    FiUpload,
    FiX,
    FiAlertCircle,
    FiPackage,
    FiTruck,
    FiFileText,
    FiEye,
} from "react-icons/fi";
import { Home, PanelsTopLeft } from "lucide-react";
import { GiWoodBeam } from "react-icons/gi";
import { HiOutlineSquare3Stack3D } from "react-icons/hi2";

import DragDropUploader from "../common/DragDropUploader";
import Toast from "../common/Toast";
import Modal from "../popup/Modal";
import { BackButtonIconOnly } from "../common/BackButton";
import FileViewer from "../common/FileViewer";

const setMobileViewport = () => {
    let viewport = document.querySelector("meta[name=viewport]");

    if (!viewport) {
        viewport = document.createElement("meta");
        viewport.name = "viewport";
        document.head.appendChild(viewport);
    }

    viewport.setAttribute(
        "content",
        "width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover"
    );
};

const resetViewport = () => {
    const viewport = document.querySelector("meta[name=viewport]");

    if (viewport) {
        viewport.setAttribute(
            "content",
            "width=device-width, initial-scale=1"
        );
    }
};

// ─── Tiny shared helpers ──────────────────────────────────────────────────────

const formatDateDriver = (dateString) => {
    if (!dateString) return "Not set";
    return new Date(dateString).toLocaleDateString("en-AU", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

const formatDateTime = (dateString) => {
    if (!dateString) return "Not set";
    return new Date(dateString).toLocaleDateString("en-AU", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

/** A simple label + value row used throughout the driver cards. */
const DetailRow = ({ label, value, link }) => (
    <div className="flex flex-col gap-0.5">
        <p className="text-xs text-gray-500">{label}</p>
        {link ? (
            <a
                href={link}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium text-blue-600 break-words"
            >
                {value || "Not specified"}
            </a>
        ) : (
            <p className="text-sm font-medium text-gray-800 break-words">
                {value || "Not specified"}
            </p>
        )}
    </div>
);

/** Quote-type icon used in the delivery card header. */
const QuoteIcon = ({ quoteType, className = "w-4 h-4" }) => {
    if (quoteType === "roof") return <Home className={className} />;
    if (quoteType === "wall") return <PanelsTopLeft className={className} />;
    if (quoteType === "floor") return <HiOutlineSquare3Stack3D className={className} />;
    if (quoteType === "timber") return <GiWoodBeam className={className} />;
    return <FiPackage className={className} />;
};

const QUOTE_TYPE_COLORS = {
    roof: { icon: "bg-blue-100 text-blue-600", border: "border-blue-100" },
    wall: { icon: "bg-red-100 text-red-600", border: "border-red-100" },
    floor: { icon: "bg-green-100 text-green-600", border: "border-green-100" },
    timber: { icon: "bg-amber-100 text-amber-600", border: "border-amber-100" },
};

const STATUS_BADGE = {
    not_scheduled: "bg-gray-100 text-gray-600",
    scheduled: "bg-amber-100 text-amber-700",
    in_transit: "bg-blue-100 text-blue-700",
    delivered: "bg-green-100 text-green-700",
    cancelled: "bg-red-100 text-red-700",
};

// ID FINDER
const UUID_REGEX =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const getDeliveryFromShortId = async (shortId, companyId) => {
    const deliveryId = shortIdToUuid(shortId);

    const { data, error } = await supabase
        .from("job_deliveries")
        .select(`
            *,
            jobs!inner (
                id,
                company_id,
                deleted
            )
        `)
        .eq("id", deliveryId)
        .eq("jobs.company_id", companyId)
        .eq("jobs.deleted", false)
        .single();

    if (error) throw error;

    return data;
};


// ─── Main component ───────────────────────────────────────────────────────────

const DriverAssignmentPage = () => {
    const { userProfile, company } = useAuth();
    const { companySlug, jobId } = useParams();
    const navigate = useNavigate();

    // ── Data ─────────────────────────────────────────────────────────────────
    const [assignment, setAssignment] = useState(null);
    const [targetDeliveryId, setTargetDeliveryId] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const isNavigatingAway = useRef(false);

    const photoChangeHandlersRef = useRef({});

    // ── Delivery action state ─────────────────────────────────────────────────
    // Keys are `${quoteType}-${deliveryCount}-${childId || ''}`
    const [uploadedPhotos, setUploadedPhotos] = useState({});
    const [clearPhotos, setClearPhotos] = useState({});
    const [deliveryNotes, setDeliveryNotes] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [fileViewerOpen, setFileViewerOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [viewerFiles, setViewerFiles] = useState([]);
    const [selectedFileIndex, setSelectedFileIndex] = useState(0);

    // ── Modals ────────────────────────────────────────────────────────────────
    const [confirmEndModal, setConfirmEndModal] = useState({
        open: false, quoteType: null, deliveryCount: 0, childId: null,
    });

    useEffect(() => {
        setMobileViewport();

        return () => {
            resetViewport();
        };
    }, []);

    // ── Fetch ─────────────────────────────────────────────────────────────────
    const fetchAssignment = useCallback(async () => {
        if (isNavigatingAway.current) return;

        try {
            setIsLoading(true);

            let realJobId = jobId;
            let resolvedDeliveryId = null;

            // Current system support:
            // /assignment/:jobId
            if (!UUID_REGEX.test(jobId)) {
                const delivery = await getDeliveryFromShortId(jobId, company.id);

                realJobId = delivery.job_id;
                resolvedDeliveryId = delivery.id;
            }

            const data = await getSingleAssignment(realJobId, company.id);

            setAssignment(data);
            setTargetDeliveryId(resolvedDeliveryId);
        } catch (err) {
            console.error("Error fetching assignment:", err);

            if (!isNavigatingAway.current) {
                Toast.error("Assignment or delivery not found");
                navigate(`/${companySlug}/assignment/list`);
            }
        } finally {
            setIsLoading(false);
        }
    }, [jobId, company?.id, companySlug, navigate]);

    useEffect(() => {
        if (company?.id && jobId) fetchAssignment();
    }, [company?.id, jobId, fetchAssignment]);

    // ── Realtime ──────────────────────────────────────────────────────────────
    useEffect(() => {
        if (!assignment?.id) return;
        const channel = subscribeToJobUpdates(assignment.id, () => fetchAssignment());
        return () => supabase.removeChannel(channel);
    }, [assignment?.id, fetchAssignment]);

    // ── Driver helpers ────────────────────────────────────────────────────────
    const getMyDeliveries = () => {
        if (!assignment?.job_deliveries) return [];

        let deliveries = assignment.job_deliveries.filter(
            d => d.assigned_driver_id === userProfile?.id
        );

        // If opened by short delivery URL, show only that exact delivery
        if (targetDeliveryId) {
            deliveries = deliveries.filter(d => d.id === targetDeliveryId);
        }

        return deliveries.sort((a, b) => {
            if (a.quote_type !== b.quote_type) {
                return a.quote_type.localeCompare(b.quote_type);
            }

            return (a.delivery_count ?? 0) - (b.delivery_count ?? 0);
        });
    };



    const deliveryKey = (quoteType, deliveryCount, childId) =>
        `${quoteType}-${deliveryCount}-${childId || ""}`;

    // ── Action handlers ───────────────────────────────────────────────────────
    const handleConfirmPackage = async (quoteType, deliveryCount, childId) => {
        try {
            if (assignment?.is_cancelled) {
                Toast.error("This job is cancelled");
                return;
            }

            await confirmPackageReceipt(assignment.id, quoteType, deliveryCount, childId);
            const quote = assignment?.job_quotes?.find((q) => q.quote_type === quoteType);
            await sendInTransitNotificationSMS(
                assignment?.customer_number,
                assignment?.customer_name,
                assignment?.short_id,
                assignment?.id,
                quote?.expected_delivery_date || null,
                company,
                assignment?.unique_id,
            );
            fetchAssignment();
        } catch (err) {
            console.error("Error confirming package:", err);
            Toast.error(err.message || "Error confirming package receipt");
        }
    };

    const handleEndDelivery = (quoteType, deliveryCount, childId) => {
        if (assignment?.is_cancelled) {
            Toast.error("This job is cancelled");
            return;
        }

        setConfirmEndModal({ open: true, quoteType, deliveryCount, childId: childId || null });
    };

    const handleEndDeliveryConfirmed = async () => {

        if (assignment?.is_cancelled) {
            Toast.error("This job is cancelled");
            return;
        }


        const { quoteType, deliveryCount, childId } = confirmEndModal;
        setConfirmEndModal((prev) => ({ ...prev, open: false }));
        try {
            const key = deliveryKey(quoteType, deliveryCount, childId);
            await completeDelivery(
                assignment.id,
                quoteType,
                userProfile.id,
                userProfile.username || userProfile.full_name,
                deliveryNotes[key] || "",
                deliveryCount,
                childId || null,
            );
            const quote = assignment?.job_quotes?.find((q) => q.quote_type === quoteType);
            await sendDeliveredNotificationSMS(
                assignment?.customer_number,
                assignment?.customer_name,
                assignment?.short_id,
                assignment?.id,
                quote?.expected_delivery_date || null,
                company,
                assignment?.unique_id,
            );
            fetchAssignment();
        } catch (err) {
            console.error("Error completing delivery:", err);
            Toast.error(err.message || "Error completing delivery");
        }
    };

    const handleUploadPhotos = async (quoteType, deliveryCount, childId) => {
        if (assignment?.is_cancelled) {
            Toast.error("This job is cancelled");
            return;
        }

        const key = deliveryKey(quoteType, deliveryCount, childId);
        const photos = uploadedPhotos[key];

        if (!photos?.length) {
            Toast.error("Please select at least one photo to upload");
            return;
        }

        setIsSubmitting(true);

        try {
            // Upload all photos in parallel
            await uploadDeliveryPhotosBulk(
                assignment.id,
                photos,
                quoteType,
                userProfile.id,
                userProfile.username || userProfile.full_name,
                userProfile.role,
                companySlug,
                deliveryCount,
                childId,
            );

            // Save delivery note once
            if (deliveryNotes[key]?.trim()) {
                let q = supabase
                    .from("job_deliveries")
                    .update({
                        delivery_notes: deliveryNotes[key].trim(),
                        note_updated_at: new Date().toISOString(),
                        note_updated_by_user_id: userProfile.id,
                        note_updated_by_name: userProfile.username || userProfile.full_name,
                    })
                    .eq("job_id", assignment.id)
                    .eq("quote_type", quoteType);

                if (childId) {
                    q = q.eq("child_unique_id", childId);
                } else {
                    q = q.is("child_unique_id", null);

                    if (deliveryCount === 0) {
                        q = q.or("delivery_count.is.null,delivery_count.eq.0");
                    } else {
                        q = q.eq("delivery_count", deliveryCount);
                    }
                }

                await q;
            }

            setUploadedPhotos(prev => ({ ...prev, [key]: [] }));
            setClearPhotos(prev => ({ ...prev, [key]: true }));

            setTimeout(() => {
                setClearPhotos(prev => ({ ...prev, [key]: false }));
            }, 100);

            Toast.success(`${photos.length} photo(s) uploaded`);

            // Only refresh once
            fetchAssignment();

        } catch (err) {
            console.error("Error uploading delivery photos:", err);
            Toast.error(err.message || "Error uploading delivery photos");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeletePhoto = async (fileId, filename) => {
        try {
            if (assignment?.is_cancelled) {
                Toast.error("This job is cancelled");
                return;
            }


            await deleteDeliveryPhoto(fileId, filename);
            setAssignment((prev) => ({
                ...prev,
                job_files: (prev.job_files || []).filter((f) => f.id !== fileId),
            }));
        } catch (err) {
            console.error("Error deleting photo:", err);
            Toast.error("Failed to delete photo");
        }
    };

    const getPhotoChangeHandler = useCallback((quoteType, deliveryCount, childId) => {
        const key = deliveryKey(quoteType, deliveryCount, childId);

        if (!photoChangeHandlersRef.current[key]) {
            photoChangeHandlersRef.current[key] = (files) => {
                setUploadedPhotos((prev) => {
                    const current = prev[key] || [];

                    // Avoid useless state updates if same file list content
                    const sameLength = current.length === (files?.length || 0);
                    const sameFiles =
                        sameLength &&
                        current.every((file, index) => {
                            const next = files[index];
                            return (
                                next &&
                                file.name === next.name &&
                                file.size === next.size &&
                                file.lastModified === next.lastModified
                            );
                        });

                    if (sameFiles) return prev;

                    return {
                        ...prev,
                        [key]: files || [],
                    };
                });
            };
        }

        return photoChangeHandlersRef.current[key];
    }, []);

    const formatAUPhone = (value) => {
        if (!value) return "";

        let digits = String(value).replace(/\D/g, "");

        // +61xxxxxxxxx or 61xxxxxxxxx -> 0xxxxxxxxx
        if (digits.startsWith("61")) {
            digits = `0${digits.slice(2)}`;
        }

        // Mobile: 04xx xxx xxx
        if (digits.length === 10 && digits.startsWith("04")) {
            return digits.replace(/(\d{4})(\d{3})(\d{3})/, "$1 $2 $3");
        }

        // Landline/general AU: 0x xxxx xxxx
        if (digits.length === 10 && digits.startsWith("0")) {
            return digits.replace(/(\d{4})(\d{3})(\d{3})/, "$1 $2 $3");
        }

        return digits;
    };

    const openDriverFile = useCallback((file, files) => {
        const index = files.findIndex(item => item.id === file.id);

        setSelectedFile(file);
        setViewerFiles(files);
        setSelectedFileIndex(index >= 0 ? index : 0);
        setFileViewerOpen(true);
    }, []);

    // ── Loading ───────────────────────────────────────────────────────────────
    if (isLoading || !assignment) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-50">
                <div className="flex flex-col items-center gap-3">
                    <div className="w-10 h-10 border-4 border-gray-300 border-t-blue-600 rounded-full animate-spin" />
                    <p className="text-sm text-gray-500">Loading assignment…</p>
                </div>
            </div>
        );
    }
    const myDeliveries = getMyDeliveries();

    const allMyAssignedDeliveries = (assignment?.job_deliveries || []).filter(
        delivery => delivery.assigned_driver_id === userProfile?.id
    );

    const visibleDriverFiles = (assignment?.job_files || []).filter(file => {
        if (
            file.file_type !== "driver" ||
            file.filename === "__folder_placeholder__"
        ) {
            return false;
        }

        // Files without a child restriction are visible to any driver
        // who is assigned to at least one delivery on this job.
        if (!file.child_id) {
            return allMyAssignedDeliveries.length > 0;
        }

        // Child-specific files are visible only when this driver is
        // assigned to the matching child delivery.
        return allMyAssignedDeliveries.some(delivery =>
            delivery.child_id === file.child_id ||
            delivery.child_unique_id === file.child_id
        );
    });

    const qColors = (qt) => QUOTE_TYPE_COLORS[qt] || {
        icon: "bg-gray-100 text-gray-600",
        border: "border-gray-100"
    };

    const isJobCancelled = !!assignment?.is_cancelled;

    const visibleDeliveries = targetDeliveryId
        ? myDeliveries.filter(d => d.id === targetDeliveryId)
        : myDeliveries;
    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <>
            <div className="mobile-detail-page driver-job-page min-h-screen bg-gray-50 pb-10">

                {/* ── Sticky header ─────────────────────────────────────────── */}
                <div className="sticky top-0 z-50 bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3">
                    <BackButtonIconOnly className="bg-transparent !text-blue-600 border border-blue-500 hover:!text-white w-10 h-10 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                        <h1 className="text-base font-bold text-gray-900 leading-tight truncate">
                            {assignment.unique_id}
                        </h1>
                        <p className="text-xs text-gray-500 truncate font-semibold">
                            <span className="font-normal text-zinc-600 mr-1">#</span>
                            {assignment?.short_id}
                        </p>
                    </div>
                    {/* Quick status pill */}
                    {visibleDeliveries.length > 0 && (
                        <span className="text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200 px-2 py-1 rounded-full flex-shrink-0">
                            {visibleDeliveries.length} delivery{myDeliveries.length !== 1 ? " jobs" : ""}
                        </span>
                    )}
                </div>

                {isJobCancelled && (
                    <div className="mx-4 mt-4 rounded-xl border border-red-300 bg-red-50 p-4 flex items-start gap-3">
                        <div className="w-9 h-9 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                            <FiAlertCircle className="w-5 h-5 text-red-700" />
                        </div>
                        <div>
                            <p className="text-sm font-bold text-red-800">This job is cancelled</p>
                            <p className="text-xs text-red-700 mt-0.5">
                                Delivery actions are disabled for this job.
                            </p>
                        </div>
                    </div>
                )}

                <div className="px-4 py-4 space-y-4">

                    {/* ── Customer info card ─────────────────────────────────── */}
                    <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
                        <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                            Customer
                        </h2>

                        <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                                <FiUser className="w-4 h-4 text-blue-600" />
                            </div>
                            <div className="min-w-0">
                                <p className="text-sm font-semibold text-gray-900 truncate">
                                    {assignment.customer_name || "Unknown"}
                                </p>
                                {assignment.customer_number && (
                                    <a
                                        href={`tel:${assignment.customer_number}`}
                                        className="text-sm text-blue-600 flex items-center gap-1 mt-0.5"
                                    >
                                        <FiPhone className="w-3.5 h-3.5 flex-shrink-0" />
                                        {formatAUPhone(assignment.customer_number)}
                                    </a>
                                )}
                            </div>
                        </div>

                        {assignment.customer_address && (
                            <div className="bg-gray-50 rounded-lg border border-gray-100 p-3">
                                <div className="flex items-start gap-2">
                                    <FiMapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm text-gray-800 break-words">
                                            {assignment.customer_address}
                                        </p>
                                        <a
                                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(assignment.customer_address)}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-xs text-green-600 flex items-center gap-1 mt-1.5 font-medium"
                                        >
                                            <FiMapPin className="w-3 h-3" />
                                            Open in Maps
                                        </a>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Supervisor quick-dial */}
                        {assignment.carpenter_supervisor_number && (
                            <div className="flex items-center gap-2 pt-1 border-t border-gray-100">
                                <FiUser className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                                <span className="text-xs text-gray-500 flex-shrink-0">Supervisor:</span>
                                <a
                                    href={`tel:${assignment.carpenter_supervisor_number}`}
                                    className="text-xs text-blue-600 font-medium truncate"
                                >
                                    {assignment.carpenter_supervisor_name || formatAUPhone(assignment.carpenter_supervisor_number)}
                                </a>
                            </div>
                        )}
                    </div>

                    {visibleDriverFiles.length > 0 && (
                        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                            <div className="flex items-start gap-3 px-4 py-3 border-b border-gray-100 bg-gray-50">
                                <div className="w-9 h-9 rounded-lg bg-gray-800 text-white flex items-center justify-center shrink-0">
                                    <FiFileText className="w-4 h-4" />
                                </div>

                                <div>
                                    <h3 className="text-sm font-semibold text-gray-800">
                                        Driver Files
                                    </h3>

                                    <p className="text-xs text-gray-500 mt-0.5">
                                        Files provided for your assigned delivery.
                                    </p>
                                </div>
                            </div>

                            <div className="p-3 space-y-2">
                                {visibleDriverFiles.map(file => (
                                    <button
                                        key={file.id}
                                        type="button"
                                        onClick={() => openDriverFile(file, visibleDriverFiles)}
                                        className="w-full flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-3 py-3 text-left active:bg-gray-50 [touch-action:manipulation]"
                                    >
                                        <div className="w-9 h-9 rounded-lg bg-gray-100 text-gray-600 flex items-center justify-center shrink-0">
                                            <FiFileText className="w-4 h-4" />
                                        </div>

                                        <div className="min-w-0 flex-1">
                                            <p className="text-sm font-medium text-gray-800 truncate">
                                               {`${file.child_unique_id !== null ? `[${file.child_unique_id}]`: ""}`} {file.original_name || file.filename}
                                            </p>

                                            <p className="text-xs text-gray-500 truncate">
                                                Uploaded by {file.uploaded_by_name || "Office"}
                                            </p>
                                        </div>

                                        <FiEye className="w-4 h-4 text-gray-400 shrink-0" />
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* ── No deliveries ──────────────────────────────────────── */}
                    {visibleDeliveries.length === 0 && (
                        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
                            <FiTruck className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                            <p className="text-sm font-medium text-gray-500">No deliveries assigned to you</p>
                        </div>
                    )}

                    {/* ── Delivery cards ─────────────────────────────────────── */}
                    {visibleDeliveries.map((delivery) => {
                        const { quote_type: quoteType, delivery_count: deliveryCount = 0 } = delivery;
                        const key = deliveryKey(quoteType, deliveryCount, delivery.child_unique_id);
                        const ordinal =
                            deliveryCount === 0
                                ? ""
                                : deliveryCount === 1
                                    ? "2nd "
                                    : deliveryCount === 2
                                        ? "3rd "
                                        : `${deliveryCount + 1}th `;

                        // Find matching job_quote row for the date/note
                        const quote = delivery.child_id
                            ? assignment.job_quotes?.find(q => q.id === delivery.child_id)
                            : assignment.job_quotes?.find(q =>
                                q.quote_type === quoteType &&
                                !q.child_unique_id &&
                                (
                                    q.delivery_count === deliveryCount ||
                                    (
                                        deliveryCount === 0 &&
                                        (q.delivery_count === null || q.delivery_count === 0)
                                    )
                                )
                            );

                        // Photos scoped to this delivery
                        const deliveryPhotos =
                            (assignment.job_files || []).filter((f) => {
                                if (f.file_type !== "delivery" || f.quote_type !== quoteType) return false;
                                if (delivery.child_unique_id) return f.child_id === delivery.child_unique_id;
                                if (f.child_id) return false;
                                if (deliveryCount === 0)
                                    return (
                                        f.delivery_count === 0 ||
                                        f.delivery_count === null ||
                                        f.delivery_count === undefined
                                    );
                                return f.delivery_count === deliveryCount;
                            }) || [];

                        const status = isJobCancelled ? "cancelled" : (delivery.delivery_status || "not_scheduled");
                        const c = qColors(quoteType);

                        return (
                            <div
                                key={key}
                                className={`rounded-xl border overflow-hidden ${isJobCancelled
                                    ? "bg-red-50 border-red-300 opacity-80"
                                    : "bg-white border-gray-200"
                                    }`}
                            >
                                {/* Card header */}
                                <div className={`px-4 py-3 flex items-center justify-between gap-3 border-b ${c.border} bg-gray-50`}>
                                    <div className="flex items-center gap-2.5 min-w-0">
                                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${c.icon}`}>
                                            <QuoteIcon quoteType={quoteType} className="w-4 h-4" />
                                        </div>
                                        <div className="min-w-0">
                                            <h3 className="text-sm font-bold text-gray-900 capitalize truncate">
                                                {ordinal}{quoteType}
                                                {quote?.child_unique_id ? ` (${quote.child_unique_id})` : ""}{" "}
                                                Delivery
                                            </h3>
                                        </div>
                                    </div>
                                    <span
                                        className={`text-xs font-semibold px-2.5 py-1 rounded-full flex-shrink-0 ${STATUS_BADGE[status] || STATUS_BADGE.not_scheduled
                                            }`}
                                    >
                                        {status.replace(/_/g, " ").toUpperCase()}
                                    </span>
                                </div>

                                {/* Card body */}
                                <div className="p-4 space-y-3">
                                    {/* Date + contact */}
                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="bg-gray-50 rounded-lg border border-gray-100 p-3">
                                            <div className="flex items-center gap-1.5 mb-2">
                                                <FiCalendar className="w-3.5 h-3.5 text-gray-400" />
                                                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                                    Schedule
                                                </span>
                                            </div>
                                            <DetailRow
                                                label="Delivery Date"
                                                value={formatDateDriver(
                                                    delivery.delivery_date || quote?.expected_delivery_date,
                                                )}
                                            />
                                        </div>

                                        <div className="bg-gray-50 rounded-lg border border-gray-100 p-3">
                                            <div className="flex items-center gap-1.5 mb-2">
                                                <FiUser className="w-3.5 h-3.5 text-gray-400" />
                                                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                                    Contact
                                                </span>
                                            </div>
                                            <DetailRow
                                                label="Phone"
                                                value={delivery.delivery_contact_phone || formatAUPhone(assignment.customer_number)}
                                                link={`tel:${delivery.delivery_contact_phone || assignment.customer_number}`}
                                            />
                                        </div>
                                    </div>

                                    {/* Address */}
                                    <div className="bg-gray-50 rounded-lg border border-gray-100 p-3">
                                        <div className="flex items-center gap-1.5 mb-2">
                                            <FiMapPin className="w-3.5 h-3.5 text-gray-400" />
                                            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                                Delivery Address
                                            </span>
                                        </div>
                                        <p className="text-sm text-gray-700 whitespace-pre-wrap">
                                            {assignment.customer_address || "Not specified"}
                                        </p>
                                        <a
                                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(assignment.customer_address)}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-xs text-green-600 flex items-center gap-1 mt-2 font-medium"
                                        >
                                            <FiMapPin className="w-3 h-3" />
                                            Open in Maps
                                        </a>
                                    </div>

                                    {/* Delivery note / special instructions */}
                                    {quote?.delivery_note && (
                                        <div className="bg-amber-50 rounded-lg border border-amber-200 p-3">
                                            <div className="flex items-center gap-2 mb-1">
                                                <FiAlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0" />
                                                <span className="text-xs font-semibold text-amber-800 uppercase tracking-wider">
                                                    Delivery Note
                                                </span>
                                            </div>
                                            <p className="text-sm text-red-600 font-semibold">
                                                {quote.delivery_note}
                                            </p>
                                        </div>
                                    )}

                                    {delivery.special_instructions && (
                                        <div className="bg-amber-50 rounded-lg border border-amber-200 p-3">
                                            <div className="flex items-center gap-2 mb-1">
                                                <FiAlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0" />
                                                <span className="text-xs font-semibold text-amber-800 uppercase tracking-wider">
                                                    Instructions
                                                </span>
                                            </div>
                                            <p className="text-sm text-amber-800">{delivery.special_instructions}</p>
                                        </div>
                                    )}

                                    {/* ── Action flow ────────────────────────────────── */}
                                    <div className="pt-3 border-t border-gray-100 space-y-3">
                                        {/* Step 1 — Confirm package */}
                                        {(!status || status === "scheduled") && (
                                            <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                                                <div className="flex items-center gap-2.5 mb-3">
                                                    <div className="w-8 h-8 bg-gray-700 rounded-lg flex items-center justify-center flex-shrink-0">
                                                        <FiPackage className="w-4 h-4 text-white" />
                                                    </div>
                                                    <div>
                                                        <h4 className="text-sm font-semibold text-gray-800">
                                                            Step 1: Confirm Package
                                                        </h4>
                                                        <p className="text-xs text-gray-500">
                                                            Confirm you have the {quoteType} package
                                                        </p>
                                                    </div>
                                                </div>
                                                <button
                                                    onClick={() =>
                                                        handleConfirmPackage(
                                                            quoteType,
                                                            deliveryCount,
                                                            delivery.child_unique_id || null,
                                                        )
                                                    }
                                                    onTouchEnd={(e) => {
                                                        e.preventDefault();
                                                        handleConfirmPackage(
                                                            quoteType,
                                                            deliveryCount,
                                                            delivery.child_unique_id || null,
                                                        );
                                                    }}
                                                    className="w-full py-3 bg-gray-800 text-white text-sm font-semibold rounded-lg transition-all min-h-[48px] [touch-action:manipulation] active:bg-gray-700"
                                                >
                                                    I Have the Package
                                                </button>
                                            </div>
                                        )}

                                        {/* Step 2 — Upload photos */}
                                        {status === "in_transit" && (
                                            <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                                                <div className="flex items-center gap-2.5 mb-3">
                                                    <div className="w-8 h-8 bg-gray-700 rounded-lg flex items-center justify-center flex-shrink-0">
                                                        <FiUpload className="w-4 h-4 text-white" />
                                                    </div>
                                                    <div>
                                                        <h4 className="text-sm font-semibold text-gray-800">
                                                            Step 2: Upload Delivery Photos
                                                        </h4>
                                                        <p className="text-xs text-gray-500">
                                                            Take photos of the delivered {quoteType} package
                                                        </p>
                                                    </div>
                                                </div>

                                                <div className="space-y-3">
                                                    <DragDropUploader
                                                        onFilesChange={getPhotoChangeHandler(
                                                            quoteType,
                                                            deliveryCount,
                                                            delivery.child_unique_id || null,
                                                        )}
                                                        clearFiles={clearPhotos[key]}
                                                        acceptedTypes=".jpg,.jpeg,.png,.heic,.heif"
                                                    />

                                                    <textarea
                                                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none text-sm"
                                                        value={deliveryNotes[key] || ""}
                                                        onChange={(e) =>
                                                            setDeliveryNotes((prev) => ({
                                                                ...prev,
                                                                [key]: e.target.value,
                                                            }))
                                                        }
                                                        rows="2"
                                                        placeholder={`Add notes about the ${quoteType} delivery…`}
                                                    />

                                                    <button
                                                        onClick={() =>
                                                            handleUploadPhotos(
                                                                quoteType,
                                                                deliveryCount,
                                                                delivery.child_unique_id || null,
                                                            )
                                                        }
                                                        onTouchEnd={(e) => {
                                                            e.preventDefault();
                                                            handleUploadPhotos(
                                                                quoteType,
                                                                deliveryCount,
                                                                delivery.child_unique_id || null,
                                                            );
                                                        }}
                                                        disabled={isSubmitting || !uploadedPhotos[key]?.length}
                                                        className="w-full py-3 bg-gray-800 text-white text-sm font-semibold rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 min-h-[48px] [touch-action:manipulation]"
                                                    >
                                                        {isSubmitting ? (
                                                            <>
                                                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                                                Uploading…
                                                            </>
                                                        ) : (
                                                            <>
                                                                <FiUpload className="w-4 h-4" />
                                                                Upload Photos
                                                            </>
                                                        )}
                                                    </button>

                                                    {/* Already-uploaded photos */}
                                                    {deliveryPhotos.length > 0 && (
                                                        <div className="pt-3 border-t border-gray-200">
                                                            <p className="text-xs font-medium text-gray-600 mb-2">
                                                                Uploaded Photos ({deliveryPhotos.length})
                                                            </p>
                                                            <div className="grid grid-cols-3 gap-2 mb-3">
                                                                {deliveryPhotos.map((photo, i) => {
                                                                    const { data: { publicUrl } } =
                                                                        supabase.storage
                                                                            .from("delivery-uploads")
                                                                            .getPublicUrl(photo.filename);
                                                                    return (
                                                                        <div
                                                                            key={i}
                                                                            className="relative aspect-square bg-gray-50 rounded-lg border border-gray-200 overflow-hidden group"
                                                                        >
                                                                            <img
                                                                                src={publicUrl}
                                                                                alt={`Photo ${i + 1}`}
                                                                                className="w-full h-full object-contain p-1"
                                                                            />
                                                                            <button
                                                                                onClick={() =>
                                                                                    handleDeletePhoto(photo.id, photo.filename)
                                                                                }
                                                                                className="absolute top-1 right-1 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                                                            >
                                                                                <FiX className="w-3.5 h-3.5" />
                                                                            </button>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>

                                                            {/* Step 3 — End delivery (only after photos) */}
                                                            <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                                                                <div className="flex items-center gap-2.5 mb-3">
                                                                    <div className="w-7 h-7 bg-gray-700 rounded-lg flex items-center justify-center flex-shrink-0">
                                                                        <FiCheckCircle className="w-3.5 h-3.5 text-white" />
                                                                    </div>
                                                                    <div>
                                                                        <h4 className="text-sm font-semibold text-gray-800">
                                                                            Step 3: Complete Delivery
                                                                        </h4>
                                                                        <p className="text-xs text-gray-500">
                                                                            Finalise the {quoteType} delivery
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                                <button
                                                                    onClick={() =>
                                                                        handleEndDelivery(
                                                                            quoteType,
                                                                            deliveryCount,
                                                                            delivery.child_unique_id || null,
                                                                        )
                                                                    }
                                                                    onTouchEnd={(e) => {
                                                                        e.preventDefault();
                                                                        handleEndDelivery(
                                                                            quoteType,
                                                                            deliveryCount,
                                                                            delivery.child_unique_id || null,
                                                                        );
                                                                    }}
                                                                    className="w-full py-3 bg-gray-800 text-white text-sm font-semibold rounded-lg transition-all min-h-[48px] [touch-action:manipulation] active:bg-gray-700"
                                                                >
                                                                    End Delivery
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {/* Delivered — summary */}
                                        {status === "delivered" && (
                                            <div className="bg-emerald-50 rounded-lg border border-emerald-200 p-4">
                                                <div className="flex items-center gap-2.5 mb-3">
                                                    <div className="w-7 h-7 bg-emerald-600 rounded-lg flex items-center justify-center flex-shrink-0">
                                                        <FiCheckCircle className="w-3.5 h-3.5 text-white" />
                                                    </div>
                                                    <div>
                                                        <h4 className="text-sm font-semibold text-emerald-800">
                                                            Delivery Completed
                                                        </h4>
                                                        <p className="text-xs text-emerald-600">
                                                            {quoteType} delivery successfully finalised
                                                        </p>
                                                    </div>
                                                </div>

                                                {deliveryPhotos.length > 0 && (
                                                    <div className="grid grid-cols-3 gap-2 mt-2">
                                                        {deliveryPhotos.map((photo, i) => {
                                                            const { data: { publicUrl } } = supabase.storage
                                                                .from("delivery-uploads")
                                                                .getPublicUrl(photo.filename);
                                                            return (
                                                                <div key={i} className="aspect-square">
                                                                    <img
                                                                        src={publicUrl}
                                                                        alt={`Photo ${i + 1}`}
                                                                        className="w-full h-full object-cover rounded-lg border border-gray-200"
                                                                    />
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}

                                                {delivery.delivered_at && (
                                                    <p className="text-xs text-gray-500 mt-2">
                                                        Completed on {formatDateTime(delivery.delivered_at)}
                                                    </p>
                                                )}

                                                {delivery.delivery_notes && (
                                                    <div className="mt-3 p-3 bg-white rounded-lg border border-emerald-100">
                                                        <p className="text-xs font-medium text-gray-700 mb-1">
                                                            Delivery Notes:
                                                        </p>
                                                        <p className="text-xs text-gray-600">{delivery.delivery_notes}</p>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {fileViewerOpen && selectedFile && viewerFiles[selectedFileIndex] && (
                <FileViewer
                    file={selectedFile}
                    filename={viewerFiles[selectedFileIndex].filename}
                    originalName={viewerFiles[selectedFileIndex].original_name}
                    fileType={viewerFiles[selectedFileIndex].file_type}
                    files={viewerFiles}
                    currentIndex={selectedFileIndex}
                    onNavigate={(newIndex) => {
                        setSelectedFileIndex(newIndex);
                        setSelectedFile(viewerFiles[newIndex]);
                    }}
                    onClose={() => setFileViewerOpen(false)}
                    jobId={assignment.id}
                    company={companySlug}
                />
            )}

            {/* ── Confirm end-delivery modal ─────────────────────────────────── */}
            <Modal
                isOpen={confirmEndModal.open}
                onClose={() => setConfirmEndModal((p) => ({ ...p, open: false }))}
                title="Complete Delivery"
                size="sm"
            >
                <p className="text-sm text-gray-600 mb-5">
                    Are you sure you want to mark this delivery as completed? This action cannot be undone.
                </p>
                <div className="flex gap-2">
                    <button
                        onClick={() => setConfirmEndModal((p) => ({ ...p, open: false }))}
                        onTouchEnd={(e) => {
                            e.preventDefault();
                            setConfirmEndModal((p) => ({ ...p, open: false }));
                        }}
                        className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium transition-colors [touch-action:manipulation]"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleEndDeliveryConfirmed}
                        onTouchEnd={(e) => {
                            e.preventDefault();
                            handleEndDeliveryConfirmed();
                        }}
                        className="flex-1 px-4 py-2.5 bg-gray-800 hover:bg-gray-900 text-white rounded-lg text-sm font-semibold transition-colors [touch-action:manipulation]"
                    >
                        Complete Delivery
                    </button>
                </div>
            </Modal>
        </>
    );
};

export default DriverAssignmentPage;
